import React, { useRef, useState } from "react";
import { API } from "../lib/brand";

/**
 * ProtectedImage — renders an artwork preview with copy-deterrent behaviour:
 *  - Disables drag and right-click context menu
 *  - On copy/save attempt (contextmenu or dragstart), the Celtic-knot
 *    watermark fades in for ~2.5 seconds as a visible deterrent before
 *    fading back out
 *  - A small "Grá" wordmark badge with solid black background sits in
 *    the bottom-right corner at all times
 *
 * Pass an `aspectClass` (e.g. "aspect-square", "aspect-[4/5]") or any
 * `className` for the wrapper sizing.
 */
export default function ProtectedImage({
  src,
  alt = "",
  className = "",
  aspectClass = "aspect-square",
  rounded = "",
  watermarkScale = 0.45,
  badgeHeightClass = "h-7",
  imgClassName = "",
  testid,
  children,
}) {
  const [showMark, setShowMark] = useState(false);
  const timerRef = useRef(null);

  const flash = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setShowMark(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShowMark(false), 2500);
  };

  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden bg-black ${aspectClass} ${rounded} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        draggable="false"
        onContextMenu={flash}
        onDragStart={flash}
        className={`absolute inset-0 w-full h-full object-cover select-none ${imgClassName}`}
        style={{ userSelect: "none", WebkitUserSelect: "none" }}
      />

      {/* Deterrent watermark — hidden unless user attempts a copy */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
          showMark ? "opacity-100" : "opacity-0"
        }`}
      >
        <img
          src={`${API}/brand/knot.png`}
          alt=""
          style={{ width: `${watermarkScale * 100}%` }}
          className="object-contain opacity-90"
        />
        <div className="absolute inset-0 bg-black/35" />
      </div>

      {/* Permanent Grá wordmark badge — bottom right */}
      <div className="absolute bottom-3 right-3 bg-black px-2.5 py-1.5 flex items-center pointer-events-none">
        <img
          src={`${API}/brand/wordmark.png`}
          alt="Grá"
          className={`${badgeHeightClass} w-auto object-contain`}
        />
      </div>

      {children}
    </div>
  );
}
